import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { resolveColor } from "../utils/colors";
import { 
  Shield,
  Users,
  FileText,
  Calendar,
  AlertTriangle,
  Search,
  Plus,
  LogOut,
  ChevronRight,
  Clock,
  User,
  Phone,
  Mail,
  Building2,
  TrendingUp,
  Bell,
  Home,
  BarChart3,
  Target,
  ClipboardList,
  Link2,
  Edit,
  ExternalLink,
  Copy,
  Trophy,
  CheckCircle2,
  Trash2,
  ListTodo,
  X,
  Menu,
  Hospital,
  FolderOpen,
  MessageSquare,
  Gift,
  Cake,
  RefreshCw,
  Briefcase,
  FlaskConical,
  Check,
  GraduationCap,
  BookOpen,
  Star,
  FolderUp,
  ClipboardCheck,
  GitBranch
} from "lucide-react";
import { authGet, authPost, checkAuth as checkAuthUtil, logout, getAuthToken, authDelete, getUserRole, isAdvisor } from "../utils/auth";
import { ADVISOR_NAV, ADVISOR_MAIN_NAV, ADVISOR_ADMIN_NAV, navSlug } from "../config/advisorNav";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Message templates for quick copy
const REMINDER_MESSAGES = {
  birthday: `Hi (Client Name),

Wishing you a very Happy Birthday! 🎂

May this year bring you good health, happiness, and success in everything you do.

Have a wonderful day!

Warm wishes,
Prashant`,
  quarterly_simple: `Hi (Client Name),

How are you? Hope you and your family are doing well!

Take care 🙂`,
  quarterly_health: `Hi (Client Name),

Quick health reminder 💡

If your policy includes the Annual Health Check-up benefit — now might be a good time to use it! Regular check-ups can help detect issues early.

Also, if there are any updates in your health or family situation (new member, address change, etc.), do let me know so we can keep your policy records current.

Stay healthy, stay covered!`,
  quarterly_referral: `Hi (Client Name),

Hope all is well at your end!

If you know anyone in your family or friend circle who's looking for genuine insurance advice — feel free to connect them with me. I'd be happy to help them the same way I've helped you.

And as always, if you ever need any assistance with your policy, I'm just a message away.

Wishing you a healthy and secure life ahead!`,
  crosssell: `Hi (Client Name),

Hope you're doing well!

I wanted to share that apart from insurance advisory, we at Shree Finserv (Associated Partner with Beshak) also offer comprehensive wealth management and financial planning services.

Our services include:

📊 Portfolio Management
📈 Financial Planning
🏦 Tax Planning
📋 Insurance Planning
💼 Investments in Bonds, Unlisted Securities & Market Linked Debentures

Our team of Chartered Accountants brings deep expertise in managing and growing wealth — with a focus on your long-term financial goals.

If you or anyone in your family is looking for guidance on investments, tax-saving, or overall financial planning — feel free to reach out. We'd be happy to help.

Warm regards,
Prashant K Uunadkat
Shree Finserv | Beshak Verified Advisor`
};

const QUARTERLY_MESSAGE_OPTIONS = [
  { id: "quarterly_simple", label: "Simple Check-in", icon: "👋" },
  { id: "quarterly_health", label: "Health Checkup", icon: "💡" },
  { id: "quarterly_referral", label: "Referral Ask", icon: "🤝" }
];

// Stage IDs that should be excluded from "Active" leads on dashboard
const INACTIVE_STAGE_KEYWORDS = ['lost', 'closed_lost', 'lost_potential', 'lost-potential'];
const CONVERTED_STAGE_KEYWORDS = ['converted', 'won', 'policy_issued', 'policyissued'];

// Helper to check if a status/stage is inactive (lost)
const isLostStage = (status) => {
  if (!status) return false;
  const normalized = status.toLowerCase().replace(/[\s\-_]+/g, '');
  return normalized.includes('lost') || INACTIVE_STAGE_KEYWORDS.some(k => normalized === k.replace(/[\s\-_]+/g, ''));
};

// Helper to check if a status/stage is converted/won
const isConvertedStage = (status) => {
  if (!status) return false;
  const normalized = status.toLowerCase().replace(/[\s\-_]+/g, '');
  return normalized.includes('converted') || normalized === 'won' || 
    CONVERTED_STAGE_KEYWORDS.some(k => normalized === k.replace(/[\s\-_]+/g, ''));
};

// Reference/source label for a lead (associate shows the associate's name)
const getLeadSourceLabel = (lead, sources) => {
  const src = (lead.source || '').trim();
  if (!src) return null;
  if (src.toLowerCase() === 'associate') return lead.associate_name ? `Assoc: ${lead.associate_name}` : 'Associate';
  const found = (sources || []).find(s => (s.value || s.id) === src || (s.label || s.name || '').toLowerCase() === src.toLowerCase());
  return found ? (found.label || found.name) : src;
};

export default function AdvisorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [copiedQuoteId, setCopiedQuoteId] = useState(null);
  const [advisorInfo, setAdvisorInfo] = useState(location.state?.user || null);
  const [expiredPolicies, setExpiredPolicies] = useState([]);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [reminders, setReminders] = useState({ birthday_reminders: [], quarterly_reminders: [], crosssell_reminders: [], total_count: 0 });
  const [copiedReminderId, setCopiedReminderId] = useState(null);
  const [showQuarterlyOptions, setShowQuarterlyOptions] = useState(null);
  const [dashboardLeads, setDashboardLeads] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [assocTotals] = useState(null);
  const userRole = getUserRole();

  useEffect(() => {
    // If user data passed from login, use it directly
    if (location.state?.user) {
      setAdvisorInfo(location.state.user);
      fetchDashboardData();
      return;
    }
    
    // Otherwise verify auth via token
    const verifyAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        navigate("/advisor/login");
        return;
      }
      
      try {
        const user = await checkAuthUtil();
        if (!user) {
          navigate("/advisor/login");
          return;
        }
        setAdvisorInfo(user);
        fetchDashboardData();
      } catch {
        navigate("/advisor/login");
      }
    };
    
    verifyAuth();
  }, [navigate, location.state]);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats
      const statsRes = await authGet('/api/advisor/dashboard');
      
      if (!statsRes.ok) {
        if (statsRes.status === 401) {
          navigate("/advisor/login");
        }
        return;
      }
      
      setStats(statsRes.data);
      
      // Fetch upcoming renewals
      const renewalsRes = await authGet('/api/advisor/renewals?days=30');
      if (renewalsRes.ok) {
        setRenewals(renewalsRes.data.renewals || []);
      }

      // Fetch recent clients
      const clientsRes = await authGet('/api/advisor/clients');
      if (clientsRes.ok) {
        setClients(clientsRes.data.clients?.slice(0, 5) || []);
      }
      
      // Fetch saved quotes
      const quotesRes = await authGet('/api/advisor/quotes');
      if (quotesRes.ok) {
        setQuotes(quotesRes.data.quotes || []);
      }
      
      // Fetch pending tasks count
      const tasksRes = await authGet('/api/advisor/tasks/pending-count');
      if (tasksRes.ok) {
        setPendingTasksCount(tasksRes.data.total || 0);
      }
      
      // Fetch client reminders
      const remindersRes = await authGet('/api/advisor/reminders');
      if (remindersRes.ok) {
        setReminders(remindersRes.data || { birthday_reminders: [], quarterly_reminders: [], crosssell_reminders: [], total_count: 0 });
      }
      
      // Fetch leads for dashboard
      const leadsRes = await authGet('/api/advisor/leads');
      if (leadsRes.ok) {
        setDashboardLeads(leadsRes.data.leads || []);
      }
      
      // Fetch lead stages
      const stagesRes = await authGet('/api/advisor/lead-stages');
      if (stagesRes.ok) {
        setLeadStages((stagesRes.data?.stages || []).map(s => ({ ...s, color: resolveColor(s.color) })));
      }

      // Fetch lead sources (for reference badge on dashboard leads)
      const sourcesRes = await authGet('/api/advisor/settings/sources');
      if (sourcesRes.ok) {
        setLeadSources(sourcesRes.data?.sources || []);
      }
      
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const fetchExpiredPolicies = async () => {
    try {
      const res = await authGet('/api/advisor/policies/expired');
      if (res.ok) {
        setExpiredPolicies(res.data.policies || []);
        setShowExpiredModal(true);
      }
    } catch (error) {
      console.error("Error fetching expired policies:", error);
    }
  };

  const copyQuoteLink = async (quoteId) => {
    const link = `${window.location.origin}/quote/view/${quoteId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedQuoteId(quoteId);
      setTimeout(() => setCopiedQuoteId(null), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedQuoteId(quoteId);
      setTimeout(() => setCopiedQuoteId(null), 2000);
    }
  };

  const openQuoteLink = (quoteId) => {
    window.open(`${window.location.origin}/quote/view/${quoteId}`, "_blank");
  };

  // Copy reminder message with client name filled in
  const copyReminderMessage = async (clientName, messageType, reminderId) => {
    const template = REMINDER_MESSAGES[messageType];
    if (!template) return;
    
    const message = template.replace(/\(Client Name\)/g, clientName);
    
    try {
      await navigator.clipboard.writeText(message);
      setCopiedReminderId(reminderId);
      setTimeout(() => setCopiedReminderId(null), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = message;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedReminderId(reminderId);
      setTimeout(() => setCopiedReminderId(null), 2000);
    }
  };

  // Dismiss a reminder (mark as sent)
  const dismissReminder = async (reminder, reminderType) => {
    try {
      const res = await authPost(`${API_URL}/api/advisor/reminders/dismiss`, {
        client_id: reminder.client_id,
        reminder_type: reminderType,
        reminder_key: reminder.reminder_key,
        policy_number: reminder.policy_number,
        quarter: reminder.quarter_number
      });
      
      if (res.ok) {
        // Remove from local state immediately
        setReminders(prev => ({
          ...prev,
          birthday_reminders: reminderType === 'birthday' 
            ? prev.birthday_reminders.filter(r => r.reminder_key !== reminder.reminder_key)
            : prev.birthday_reminders,
          quarterly_reminders: reminderType === 'quarterly'
            ? prev.quarterly_reminders.filter(r => r.reminder_key !== reminder.reminder_key)
            : prev.quarterly_reminders,
          crosssell_reminders: reminderType === 'crosssell'
            ? prev.crosssell_reminders.filter(r => r.reminder_key !== reminder.reminder_key)
            : prev.crosssell_reminders,
          total_count: prev.total_count - 1
        }));
      }
    } catch (error) {
      console.error("Failed to dismiss reminder:", error);
    }
  };

  // Update lead stage from dashboard
  const handleLeadStageChange = async (leadId, newStageId) => {
    try {
      const token = getAuthToken();
      const res = await authGet(`/api/advisor/leads/${leadId}`);
      if (res.ok) {
        const leadData = res.data.lead;
        const updateRes = await fetch(`${API_URL}/api/advisor/leads/${leadId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ ...leadData, status: newStageId })
        });
        
        if (updateRes.ok) {
          // Update local state immediately for better UX
          setDashboardLeads(prev => prev.map(lead => 
            lead.id === leadId ? { ...lead, status: newStageId } : lead
          ));
        }
      }
    } catch (error) {
      console.error("Failed to update lead stage:", error);
    }
  };

  // Get stage info by ID
  const getStageById = (stageId) => {
    return leadStages.find(s => s.id === stageId) || { name: stageId, color: '#6b7280' };
  };

  const activeLeadsCount = dashboardLeads.filter(lead => {
    const stageName = getStageById(lead.status)?.name || '';
    return !isLostStage(lead.status) && !isLostStage(stageName) &&
           !isConvertedStage(lead.status) && !isConvertedStage(stageName);
  }).length;

  // Dark text on light stage colors, white text on dark ones
  const stagePillText = (hex) => {
    if (!hex || !hex.startsWith('#') || hex.length < 7) return 'white';
    const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? '#14213D' : 'white';
  };

  const deleteQuote = async (quoteId, clientName) => {
    if (!window.confirm(`Are you sure you want to delete the quote for "${clientName || 'Unnamed Client'}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const res = await authDelete(`/api/advisor/quotes/${quoteId}`);
      if (res.ok) {
        // Remove from local state
        setQuotes(quotes.filter(q => q.id !== quoteId));
      } else {
        alert("Failed to delete quote. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting quote:", error);
      alert("Failed to delete quote. Please try again.");
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    navigate(`/advisor/clients?search=${encodeURIComponent(searchQuery)}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntilRenewal = (endDate) => {
    const end = new Date(endDate);
    const today = new Date();
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#3A4557]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden bg-white/95 backdrop-blur-xl sticky top-0 z-40 safe-area-inset-top">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowSidebar(true)}
              className="p-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div>
              <p className="text-[#1B2A4A] font-semibold">Hi, {advisorInfo?.name?.split(' ')[0] || "User"}</p>
              <p className="text-[#3A4557] text-xs">{isAdvisor() ? 'Admin' : 'Employee'}</p>
            </div>
          </div>
          <button 
            onClick={() => navigate("/advisor/leads")}
            className="relative p-2 bg-[#EDF1F8] rounded-xl"
          >
            <Bell className="h-5 w-5 text-[#3A4557]" />
            {pendingTasksCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                {pendingTasksCount > 9 ? '9+' : pendingTasksCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block bg-[#EDF1F8]/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                <Shield className="h-5 w-5 text-[#0D1A3A]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#1B2A4A]">Advisor CRM</h1>
                <p className="text-[#3A4557] text-sm">Welcome, {advisorInfo?.name || "Advisor"}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <Home className="h-4 w-4 mr-2" />
                Website
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Quick Stats */}
      <div className="md:hidden px-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={() => navigate("/advisor/leads")}
            className="bg-blue-500/10 rounded-2xl p-4 border border-blue-500/30 text-left active:scale-95 transition-transform"
          >
            <div className="flex items-center justify-between mb-2">
              <Target className="h-5 w-5 text-blue-600" />
              <ChevronRight className="h-4 w-4 text-blue-500/50" />
            </div>
            <p className="text-2xl font-bold text-blue-600">{activeLeadsCount}</p>
            <p className="text-blue-600/70 text-xs">Active Leads</p>
          </button>
          
          <button 
            onClick={fetchExpiredPolicies}
            className="bg-red-500/10 rounded-2xl p-4 border border-red-500/30 text-left active:scale-95 transition-transform"
          >
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <ChevronRight className="h-4 w-4 text-red-500/50" />
            </div>
            <p className="text-2xl font-bold text-red-600">{stats?.expired_policies || 0}</p>
            <p className="text-red-600/70 text-xs">Expired</p>
          </button>
          
          <button 
            onClick={() => navigate("/advisor/renewals")}
            className="bg-[#EDF1F8]/50 rounded-2xl p-4 border border-[#C9D4E6]/50 text-left active:scale-95 transition-transform"
          >
            <div className="flex items-center justify-between mb-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              <ChevronRight className="h-4 w-4 text-[#3A4557]" />
            </div>
            <p className="text-2xl font-bold text-[#1B2A4A]">{stats?.upcomingRenewals || 0}</p>
            <p className="text-[#3A4557] text-xs">Renewals</p>
          </button>
          
          <button 
            onClick={() => navigate("/advisor/map-policy")}
            className="bg-green-500/10 rounded-2xl p-4 border border-green-500/30 text-left active:scale-95 transition-transform"
          >
            <div className="flex items-center justify-between mb-2">
              <FileText className="h-5 w-5 text-green-600" />
              <Plus className="h-4 w-4 text-green-500/50" />
            </div>
            <p className="text-lg font-bold text-green-600">New</p>
            <p className="text-green-600/70 text-xs">Map Policy</p>
          </button>
        </div>
      </div>

      {/* Training Academy Quick Access */}
      <div className="md:hidden px-4 pb-4 space-y-3">
        <button
          onClick={() => navigate("/advisor/directory")}
          className="w-full bg-gradient-to-r from-[#D4A017]/10 to-[#D9A82C]/10 border border-[#D4A017]/30 rounded-2xl p-4 flex items-center gap-4 active:scale-[0.98] transition-transform"
          data-testid="dashboard-directory-mobile-btn"
        >
          <div className="w-12 h-12 rounded-xl bg-[#D4A017]/20 flex items-center justify-center shrink-0">
            <BookOpen className="h-6 w-6 text-[#A87608]" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-[#1B2A4A] font-semibold text-sm">Directory</p>
            <p className="text-[#3A4557] text-xs">Contacts, insurer portals & quotation platforms</p>
          </div>
          <ChevronRight className="h-5 w-5 text-[#A87608]/50 shrink-0" />
        </button>
        <button
          onClick={() => navigate("/advisor/training")}
          className="w-full bg-gradient-to-r from-[#D4A017]/10 to-[#D9A82C]/10 border border-orange-500/30 rounded-2xl p-4 flex items-center gap-4 active:scale-[0.98] transition-transform"
          data-testid="dashboard-training-academy-btn"
        >
          <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center shrink-0">
            <GraduationCap className="h-6 w-6 text-orange-600" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-[#1B2A4A] font-semibold text-sm">Training Academy</p>
            <p className="text-[#3A4557] text-xs">Learn insurance from scratch — modules, quizzes & more</p>
          </div>
          <ChevronRight className="h-5 w-5 text-orange-600/50 shrink-0" />
        </button>
      </div>

      {/* Mobile Recent Clients — removed per user request */}

      {/* Desktop Content */}
      <main className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Quick Actions — rendered from the shared nav config in the exact deployed desktop order */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="desktop-nav-toolbar">
          {ADVISOR_NAV.filter((i) => !i.desktopHidden && (!i.adminOnly || isAdvisor())).map((item) => (
            item.primary ? (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-full shadow-lg shadow-green-500/20 px-6"
                data-testid={`nav-${navSlug(item.path)}`}
              >
                <item.icon className="h-4 w-4 mr-2" />
                {item.label}
              </Button>
            ) : (
              <Button
                key={item.path}
                variant="outline"
                onClick={() => navigate(item.path)}
                className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8] rounded-full"
                data-testid={`nav-${navSlug(item.path)}`}
              >
                <item.icon className={`h-4 w-4 mr-2 ${item.color}`} />
                {item.label}
              </Button>
            )
          ))}
        </div>

        {/* Search Bar */}
        <Card className="bg-white/50 border-[#C9D4E6] mb-8">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A4557]" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search clients by name, mobile, or email..."
                  className="pl-10 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                />
              </div>
              <Button
                onClick={handleSearch}
                className="bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] px-6"
              >
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid — actionable metrics only (clients/policies/premium live in Business Analytics) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#3A4557] text-sm">Renewals (30 days)</p>
                  <p className="text-3xl font-bold text-[#A87608] mt-1">{stats?.renewals_30_days || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-[#D4A017]/15 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-[#A87608]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-white/50 border-[#C9D4E6] cursor-pointer hover:bg-[#EDF1F8]/50 transition-colors"
            onClick={fetchExpiredPolicies}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#3A4557] text-sm">Expired</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{stats?.expired_policies || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              {stats?.expired_policies > 0 && (
                <p className="text-xs text-[#3A4557] mt-2">Click to view details</p>
              )}
            </CardContent>
          </Card>

          <Card 
            className="bg-white/50 border-[#C9D4E6] cursor-pointer hover:bg-[#EDF1F8]/50 transition-colors"
            onClick={() => navigate("/advisor/leads")}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#3A4557] text-sm">Active Leads</p>
                  <p className="text-3xl font-bold text-[#1B2A4A] mt-1">{activeLeadsCount}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-blue-500/15 flex items-center justify-center">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Client Reminders Section — removed per user request */}

        {/* Active Leads Section - Excludes Lost and Converted */}
        {dashboardLeads.filter(lead => {
          const stageName = getStageById(lead.status)?.name || '';
          return !isLostStage(lead.status) && !isLostStage(stageName) &&
                 !isConvertedStage(lead.status) && !isConvertedStage(stageName);
        }).length > 0 && (
          <Card className="bg-white/50 border-[#C9D4E6] mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#1B2A4A] flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#2E3B57]" />
                  Active Leads
                  <span className="ml-2 px-2 py-0.5 bg-[#EDF1F8]/40 text-[#2E3B57] text-xs rounded-full">
                    {dashboardLeads.filter(lead => {
                      const stageName = getStageById(lead.status)?.name || '';
                      return !isLostStage(lead.status) && !isLostStage(stageName) &&
                             !isConvertedStage(lead.status) && !isConvertedStage(stageName);
                    }).length}
                  </span>
                </h2>
                <Button
                  onClick={() => navigate("/advisor/leads")}
                  size="sm"
                  variant="outline"
                  className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A]"
                >
                  View All
                </Button>
              </div>

              <Swipeable hint>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#C9D4E6]">
                      <th className="text-left py-3 px-2 text-[#3A4557] font-medium">Name</th>
                      <th className="text-left py-3 px-2 text-[#3A4557] font-medium">Reference</th>
                      <th className="text-left py-3 px-2 text-[#3A4557] font-medium">Stage</th>
                      <th className="text-right py-3 px-2 text-[#3A4557] font-medium">Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardLeads
                      .filter(lead => {
                        // Filter out lost and converted leads
                        const stageName = getStageById(lead.status)?.name || '';
                        return !isLostStage(lead.status) && !isLostStage(stageName) &&
                               !isConvertedStage(lead.status) && !isConvertedStage(stageName);
                      })
                      .slice(0, 10).map((lead) => {
                      const stage = getStageById(lead.status);
                      return (
                        <tr 
                          key={lead.id} 
                          className="border-b border-[#C9D4E6] hover:bg-[#EDF1F8]/30 cursor-pointer"
                          onClick={() => navigate(`/advisor/leads?edit=${lead.id}`)}
                        >
                          <td className="py-3 px-2">
                            <span className="text-[#1B2A4A] font-medium">{lead.name}</span>
                          </td>
                          <td className="py-3 px-2">
                            {getLeadSourceLabel(lead, leadSources) ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap bg-[#EDF1F8] text-[#41527A] border border-[#C9D4E6]">
                                <GitBranch className="h-2.5 w-2.5" />
                                {getLeadSourceLabel(lead, leadSources)}
                              </span>
                            ) : (
                              <span className="text-[#94a3b8] text-xs">—</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <select
                              value={lead.status || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleLeadStageChange(lead.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer focus:ring-2 focus:ring-blue-500 min-w-[130px] appearance-none bg-no-repeat"
                              style={{ 
                                backgroundColor: stage?.color ? `${stage.color}` : '#4b5563',
                                color: stagePillText(stage?.color || '#4b5563'),
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${stagePillText(stage?.color || '#4b5563') === 'white' ? 'white' : '%2314213D'}'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                backgroundPosition: 'right 8px center',
                                backgroundSize: '16px',
                                paddingRight: '30px'
                              }}
                            >
                              {!lead.status && (
                                <option value="" style={{ backgroundColor: '#ffffff', color: '#14213D' }}>
                                  Select Stage
                                </option>
                              )}
                              {leadStages.map((s) => (
                                <option key={s.id} value={s.id} style={{ backgroundColor: '#ffffff', color: '#14213D' }}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/advisor/leads?edit=${lead.id}`); }}
                              className="p-1.5 rounded-md hover:bg-[#EDF1F8] text-[#2E3B57]"
                              title="Edit lead"
                              data-testid={`dashboard-lead-edit-${lead.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {dashboardLeads.length > 10 && (
                  <div className="text-center mt-4">
                    <Button
                      onClick={() => navigate("/advisor/leads")}
                      variant="outline"
                      size="sm"
                      className="border-[#C9D4E6] text-[#3A4557] hover:text-[#1B2A4A]"
                    >
                      View all {dashboardLeads.length} leads
                    </Button>
                  </div>
                )}
              </Swipeable>
            </CardContent>
          </Card>
        )}

        {/* My Saved Quotes Section — removed per user request */}

        <div className="grid grid-cols-1 gap-8">
          {/* Upcoming Renewals */}
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#1B2A4A] flex items-center gap-2">
                  <Bell className="h-5 w-5 text-[#A87608]" />
                  Upcoming Renewals
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/advisor/renewals")}
                  className="text-[#A87608] hover:text-[#A87608]"
                >
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {renewals.length === 0 ? (
                <div className="text-center py-8 text-[#3A4557]">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No upcoming renewals in next 30 days</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {renewals.slice(0, 5).map((policy) => {
                    const daysLeft = getDaysUntilRenewal(policy.policy_end_date);
                    return (
                      <div
                        key={policy.id}
                        onClick={() => navigate(`/advisor/clients/${policy.client_id}`)}
                        className="p-4 bg-[#EDF1F8]/50 rounded-xl hover:bg-[#EDF1F8] transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[#1B2A4A] font-medium">{policy.client?.name || "Unknown"}</p>
                            <p className="text-[#3A4557] text-sm">{policy.insurance_company} - {policy.plan_name}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${daysLeft <= 7 ? 'text-red-600' : daysLeft <= 15 ? 'text-[#A87608]' : 'text-emerald-600'}`}>
                              {daysLeft} days
                            </p>
                            <p className="text-[#3A4557] text-xs">{formatDate(policy.policy_end_date)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Clients — removed per user request */}
        </div>
      </main>

      {/* Expired Policies Modal */}
      {showExpiredModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-white border-[#C9D4E6] w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-4 border-b border-[#C9D4E6]">
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#1B2A4A]">Expired Policies</h3>
                    <p className="text-[#3A4557] text-sm">{expiredPolicies.length} policies need attention</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowExpiredModal(false)}
                  className="text-[#3A4557] hover:text-[#1B2A4A]"
                >
                  ✕
                </Button>
              </div>
              
              <div className="overflow-y-auto max-h-[60vh] p-4 space-y-3">
                {expiredPolicies.length === 0 ? (
                  <p className="text-[#3A4557] text-center py-8">No expired policies found</p>
                ) : (
                  expiredPolicies.map((policy) => (
                    <div 
                      key={policy.id}
                      data-testid={`expired-policy-${policy.id}`}
                      className="bg-[#EDF1F8]/50 rounded-lg p-4 hover:bg-[#EDF1F8] transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-4 w-4 text-[#3A4557]" />
                            <p className="text-[#1B2A4A] font-medium">{policy.proposer_name || policy.client_name || 'Unknown'}</p>
                          </div>
                          <p className="text-[#3A4557] text-sm">{policy.plan_name || 'N/A'}</p>
                          <p className="text-[#3A4557] text-xs">{policy.insurance_company}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-600 text-sm font-medium">
                            Expired: {policy.policy_end_date ? new Date(policy.policy_end_date).toLocaleDateString('en-IN') : 'N/A'}
                          </p>
                          <p className="text-[#3A4557] text-xs">{policy.policy_number}</p>
                          <p className="text-[#A87608] text-sm font-medium mt-1">
                            ₹{(policy.premium_amount || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-[#C9D4E6]">
                        <Button
                          size="sm"
                          className="bg-[#D4A017]/15 text-[#A87608] hover:bg-[#D4A017]/25 border border-[#D4A017]/40"
                          onClick={() => {
                            setShowExpiredModal(false);
                            navigate(`/advisor/map-policy?renewalOf=${policy.id}`);
                          }}
                          data-testid={`map-renewed-expired-btn-${policy.id}`}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Map Renewed Policy
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
                          onClick={() => {
                            setShowExpiredModal(false);
                            navigate(`/advisor/clients/${policy.client_id}`);
                          }}
                        >
                          View Client <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
